import React, { useState, useEffect } from "react";
import {
  BankBuildingIcon,
  PayrollCardIcon,
  UploadCloudIcon,
  CameraIcon,
  CheckIcon,
} from "../../../common/Icons";
import { applicationsApi } from "../../../services/api";
import { toast } from "react-toastify";
import {
  ApplicationStatusDto,
  ApplicationStepsDto,
  UpdateIncomeContentProps,
} from "../../../common/types";
import {
  customerInfoIdValue,
  UploadDocumentType,
} from "../../../common/helperConstants";
import { getStorage } from "../../../common/storageHelper";
import {
  AcceptsOneFileText,
  CustomerInfoIdNotFoundText,
  DataSubmittedSuccessfullyText,
  FileExtensionDoesntMatchText,
  FileSizeTooLargeText,
  FileSizeTooSmallText,
  SomethingWentWrongText,
  UploadInvalidFileTypeText,
} from "../../../common/messageContainer";
import { checkMIME, PlaidLinkWrapper } from "../../../common/helperMethods";
import CircleSpinner from "../../../common/loader/circleSpinner";
import { FileRejection, useDropzone } from "react-dropzone";

const UpdateIncomeContent: React.FC<UpdateIncomeContentProps> = ({
  onNextStep,
  mainContentClassName,
  markStepAsCompleted,
  currentStepId,
  visibleSteps,
  showSubmitButton = true,
}) => {
  const [isLoader, setIsLoader] = useState(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [plaidType, setPlaidType] = useState<"bank" | "payroll" | null>(null);
  const [uploadedBankFile, setUploadedBankFile] = useState<string | null>(null);
  const [uploadedPayrollFile, setUploadedPayrollFile] = useState<string | null>(
    null,
  );
  const [isBankUploaded, setIsBankUploaded] = useState(false);
  const [isPayrollUploaded, setIsPayrollUploaded] = useState(false);
  const [isPlaidBankConnected, setIsPlaidBankConnected] = useState(false);
  const [isPlaidPayrollConnected, setIsPlaidPayrollConnected] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const infoId = getStorage(customerInfoIdValue);
  const isLastStep =
    visibleSteps[visibleSteps.length - 1]?.id === currentStepId;

  const logPlaidEvent = (eventName: string, metadata: any) => {
    if (!infoId) {
      toast.error(CustomerInfoIdNotFoundText);
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
      return;
    }
    // fire-and-forget, never block or throw on the UI
    applicationsApi
      .logPlaidLinkEvent(infoId, {
        sessionId,
        eventName,
        viewName: metadata?.view_name ?? null,
        errorType: metadata?.error_type ?? null,
        errorCode: metadata?.error_code ?? null,
        errorMessage: metadata?.error_message ?? null,
        institutionId: metadata?.institution_id ?? null,
        institutionName: metadata?.institution_name ?? null,
        linkSessionId: metadata?.link_session_id ?? null,
        requestId: metadata?.request_id ?? null,
        timestamp: new Date().toISOString(),
        rawMetadata: JSON.stringify(metadata ?? {}),
      })
      .catch(() => {}); // never let logging break the UX
  };

  useEffect(() => {
    fetchApplicationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoId]);

  const fetchApplicationData = async () => {
    if (!infoId) {
      toast.error(CustomerInfoIdNotFoundText);
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
      return;
    }

    try {
      setIsLoader(true);
      const appData: ApplicationStatusDto =
        await applicationsApi.getApplication(infoId);

      const steps: ApplicationStepsDto = appData?.steps;

      if (steps) {
        // Also check the income step for both bank and payroll files
        const bankStep = steps.bank;
        if (bankStep) {
          const isCompleted = bankStep.completed || false;
          const filePath = bankStep.filePath || null;
          if (isCompleted && filePath) {
            setIsBankUploaded(true);
            setUploadedBankFile(filePath);
          }
          if (bankStep.count && bankStep.count === 1) {
            setIsPlaidBankConnected(true);
          }
        }

        const payrollStep = steps.payroll;
        if (payrollStep) {
          const isCompleted = payrollStep.completed || false;
          const filePath = payrollStep.filePath || null;
          if (isCompleted && filePath) {
            setIsPayrollUploaded(true);
            setUploadedPayrollFile(filePath);
          }
          if (payrollStep.count && payrollStep.count === 1) {
            setIsPlaidPayrollConnected(true);
          }
        }
      } else {
        toast.error(SomethingWentWrongText);
      }
    } catch (error: any) {
      toast.error(SomethingWentWrongText);
    } finally {
      setIsLoader(false);
    }
  };

  const fetchLinkToken = async (type: string) => {
    if (!infoId) {
      toast.error(CustomerInfoIdNotFoundText);
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
      return;
    }

    try {
      setIsLoader(true);
      const response = await applicationsApi.createPlaidLinkToken(infoId, type);
      if (response?.linkToken) {
        setLinkToken(response.linkToken);
        setSessionId(response.sessionId);
      } else {
        toast.error("Failed to initialize bank connection");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error || "Failed to initialize bank connection",
      );
    } finally {
      setIsLoader(false);
    }
  };

  const onPlaidSuccess = async (publicToken: string, metadata: any) => {
    logPlaidEvent("SUCCESS", metadata);
    if (!infoId) {
      toast.error(CustomerInfoIdNotFoundText);
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
      return;
    }

    try {
      setIsLoader(true);
      const response = await applicationsApi.exchangePlaidToken(
        infoId,
        publicToken,
        plaidType || "bank",
        sessionId,
      );

      if (response?.success) {
        const accountType = plaidType === "payroll" ? "Payroll" : "Bank";
        toast.success(`${accountType} account connected successfully!`);
        markStepAsCompleted(currentStepId);

        // Update the uploaded state for the connected account type
        if (plaidType === "bank") {
          setIsBankUploaded(true);
          setUploadedBankFile(`${accountType} Account Connected`);
        } else if (plaidType === "payroll") {
          setIsPayrollUploaded(true);
          setUploadedPayrollFile(`${accountType} Account Connected`);
        }
        fetchApplicationData();
        // Log metadata for debugging
      } else {
        toast.error(response?.message || "Failed to connect account");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ||
          "Failed to connect account. Please try again.",
      );
    } finally {
      setPlaidType(null);
      setIsLoader(false);
    }
  };

  const onPlaidExit = (err: any, metadata: any) => {
    logPlaidEvent("EXIT", {
      ...metadata,
      error_type: err?.error_type,
      error_code: err?.error_code,
      error_message: err?.error_message,
    });
    if (err) {
      toast.error("Connection cancelled or failed");
    }
    setPlaidType(null);
    setLinkToken(null);
  };

  const onPlaidEvent = (eventName: string, metadata: any) => {
    logPlaidEvent(eventName, metadata);
  };

  const handleInteraction = () => {
    markStepAsCompleted(currentStepId);
  };

  const handleAddAccountClick = (type: "bank" | "payroll") => {
    setPlaidType(type);
    if (!infoId) {
      toast.error(CustomerInfoIdNotFoundText);
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
      return;
    }

    fetchLinkToken(type);
  };

  const handleFileUpload = async (file: File, type: "bank" | "payroll") => {
    try {
      setIsLoader(true);
      const documentType =
        type === "bank"
          ? UploadDocumentType.BANK_STATEMENT
          : UploadDocumentType.PAYROLL_DOCUMENT;
      const response = await applicationsApi.uploadDocument(
        infoId || "",
        documentType,
        file,
      );
      if (response?.success) {
        toast.success(DataSubmittedSuccessfullyText);
        fetchApplicationData();
        // Update the uploaded state for the specific document type
        if (documentType === UploadDocumentType.BANK_STATEMENT) {
          setIsBankUploaded(true);
          setUploadedBankFile(file.name);
          handleInteraction();
        } else if (documentType === UploadDocumentType.PAYROLL_DOCUMENT) {
          setIsPayrollUploaded(true);
          setUploadedPayrollFile(file.name);
          handleInteraction();
        }
      } else {
        toast.error(response?.message || "Upload failed");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Failed to upload file. Please try again.",
      );
    } finally {
      setIsLoader(false);
    }
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: "bank" | "payroll",
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file, type);
    }
    // Clear the input value to allow selecting the same file again if needed
    event.target.value = "";
  };

  const handleTakePhotoClick = (inputId: string) => {
    // handleInteraction is called by handleFileChange after successful upload
    document.getElementById(inputId)?.click();
  };

  const getFileName = (filePath: string | null): string => {
    if (!filePath) return "";

    // Handle both Windows (\) and Unix (/) path separators
    const normalizedPath = filePath.replace(/\\/g, "/");
    const fileName = normalizedPath.split("/").pop() || "";

    return fileName;
  };

  const FileDropzone = ({ isLoader, docType }: any) => {
    const getDropFile = (
      acceptedFiles: any[],
      fileRejections: FileRejection[],
      event: any,
    ) => {
      setIsLoader(true);
      if (!infoId) {
        toast.error(CustomerInfoIdNotFoundText);
        setIsLoader(false);
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload();
        return;
      }
      if (fileRejections.length === 0) {
        acceptedFiles.forEach(async (selectedFile: any) => {
          const mimeType = await checkMIME(selectedFile);

          if (mimeType === selectedFile.type) {
            handleFileUpload(selectedFile, docType);
          } else {
            toast.error(FileExtensionDoesntMatchText);
            setIsLoader(false);
          }
        });
      } else {
        let errorMsg = "";
        let invMsg = "";
        let fileLarMsg = "";
        let fileSmaMsg = "";
        let multiMsg = "";
        fileRejections.forEach((fRej) => {
          fRej.errors.forEach((d: { code: string }) => {
            if (d.code === "file-invalid-type") {
              invMsg = UploadInvalidFileTypeText;
            } else if (d.code === "file-too-large") {
              fileLarMsg = FileSizeTooLargeText;
            } else if (d.code === "file-too-small") {
              fileSmaMsg = FileSizeTooSmallText;
            } else if (d.code === "too-many-files") {
              multiMsg = AcceptsOneFileText;
            }
          });
        });
        if (invMsg !== "") errorMsg += invMsg + " ";
        if (fileLarMsg !== "") errorMsg += fileLarMsg + " ";
        if (fileSmaMsg !== "") errorMsg += fileSmaMsg + " ";
        if (multiMsg !== "") errorMsg += multiMsg;

        toast.error(errorMsg);
        setIsLoader(false);
      }
    };

    const { getRootProps, getInputProps, isDragAccept } = useDropzone({
      maxFiles: 1,
      maxSize: 20971520, //20MB=20971520 bytes
      minSize: 1024, //1kb=1024 bytes
      accept: {
        "image/png": [".png"],
        "image/jpg": [".jpg"],
        "image/jpeg": [".jpeg"],
        "image/bmp": [".bmp"],
        "image/gif": [".gif"],
        "image/heic": [".heic", ".heif"],
        "application/pdf": [".pdf"],
      },
      onDrop: getDropFile,
      disabled: isLoader,
    });

    return (
      <div
        {...getRootProps()}
        className={`block border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
        ${isLoader ? "opacity-50 cursor-not-allowed" : ""}
        ${isDragAccept ? "border-blue-500 bg-blue-50" : "border-gray-300"}
      `}
      >
        <input disabled={isLoader} {...getInputProps()} />
        <UploadCloudIcon className="w-8 h-8 text-gray-400 mx-auto mb-1" />
        <p className="hidden md:block text-sm text-gray-600">
          <span className="font-semibold text-[#09517d]">Click or drag</span>{" "}
          file to upload
        </p>
        <p className="block md:hidden text-sm text-gray-600">
          <span className="font-semibold text-[#09517d]">Tap to upload</span>
        </p>
      </div>
    );
  };

  return (
    <main className={mainContentClassName || "w-full"}>
      <div className={`${isLoader === true ? "loaderGrey" : "loaderDisplay"}`}>
        <CircleSpinner />
      </div>
      <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-lg h-full">
        <h2 className="text-2xl font-semibold text-[#09517d] mb-4 sm:mb-6 text-center">
          Update Your Income
        </h2>

        {/* Previously Uploaded Files Display */}
        {(isBankUploaded || isPayrollUploaded) && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-lg font-semibold text-[#09517d] mb-4 text-center">
              Previously Uploaded Documents
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Bank Account Previously Uploaded */}
              <div className="bg-white p-4 rounded-lg border border-blue-200">
                <div className="flex items-center mb-2">
                  <BankBuildingIcon className="w-5 h-5 text-[#09517d] mr-2" />
                  <h4 className="font-semibold text-[#09517d]">Bank Account</h4>
                </div>
                {isBankUploaded && uploadedBankFile ? (
                  <div className="wdbrk text-sm text-green-700">
                    <div className="flex items-center mb-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      <span className="font-medium">Document uploaded</span>
                    </div>
                    <p className="text-gray-600 break-all">
                      {getFileName(uploadedBankFile)}
                    </p>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">
                    <div className="flex items-center mb-1">
                      <span className="w-2 h-2 bg-gray-300 rounded-full mr-2"></span>
                      <span>No document uploaded</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Payroll Account Previously Uploaded */}
              <div className="bg-white p-4 rounded-lg border border-blue-200">
                <div className="flex items-center mb-2">
                  <PayrollCardIcon className="w-5 h-5 text-[#09517d] mr-2" />
                  <h4 className="font-semibold text-[#09517d]">
                    Payroll Account
                  </h4>
                </div>
                {isPayrollUploaded && uploadedPayrollFile ? (
                  <div className="wdbrk text-sm text-green-700">
                    <div className="flex items-center mb-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      <span className="font-medium">Document uploaded</span>
                    </div>
                    <p className="text-gray-600 break-all">
                      {getFileName(uploadedPayrollFile)}
                    </p>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">
                    <div className="flex items-center mb-1">
                      <span className="w-2 h-2 bg-gray-300 rounded-full mr-2"></span>
                      <span>No document uploaded</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 xl:gap-6 lg:gap-8">
          {/* Add Bank Account Card */}
          <div className="bg-white p-4 sm:p-6 rounded-xl border border-[#09517d] flex flex-col items-center transition-shadow duration-300">
            <div className="flex items-center justify-center mb-3 sm:mb-4">
              <BankBuildingIcon className="w-6 h-6 sm:w-7 sm:h-7 text-[#09517d] mr-2" />
              <h3 className="text-lg sm:text-xl font-semibold text-[#09517d]">
                + Add Bank Account
              </h3>
            </div>
            <p className="text-sm text-gray-600 text-center mb-4 sm:mb-6">
              Verify your income by signing in to your online bank account.
            </p>

            <button
              onClick={() => handleAddAccountClick("bank")}
              disabled={isPlaidBankConnected}
              type="button"
              className={`
                  ${
                    isPlaidBankConnected
                      ? "bg-green-700 hover:bg-green-700 cursor-not-allowed"
                      : "bg-blue-500 hover:bg-blue-600"
                  }
                  text-white font-semibold py-2 sm:py-2.5 px-6 sm:px-8 rounded-lg
                  transition-all duration-200 ease-in-out
                  focus:outline-none focus:ring-2 focus:ring-opacity-50 mb-4
                  disabled:bg-green-600
                `}
              aria-label="Add Bank Account"
            >
              {isPlaidBankConnected ? (
                <div className="flex items-center gap-1">
                  <CheckIcon className="w-5 h-5 text-white flex-shrink-0" />{" "}
                  Bank Account Connected
                </div>
              ) : (
                "Add Account via Plaid"
              )}
            </button>

            {/* Upload Section for Bank Statements */}
            <div className="mt-2 pt-4 border-t border-gray-200 w-full space-y-3">
              <h4 className="text-md font-semibold text-gray-700 text-center">
                Or, Upload 3 Months of Bank Statements
              </h4>
              <FileDropzone isLoader={isLoader} docType="bank" />
              <button
                type="button"
                className="w-full flex items-center justify-center p-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors md:hidden disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleTakePhotoClick("bank-camera-input")}
                disabled={isLoader}
                aria-label="Take photo to upload bank statements"
              >
                <CameraIcon className="w-5 h-5 text-gray-500 mr-2" />
                <span className="hidden md:block text-sm font-medium text-gray-700">
                  Take Photo
                </span>
                <span className="block md:hidden text-sm font-medium text-gray-700">
                  Tap to take photo
                </span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  id="bank-camera-input"
                  className="sr-only"
                  onChange={(e) => handleFileChange(e, "bank")}
                  disabled={isLoader}
                />
              </button>
            </div>
          </div>

          {/* Add Payroll Card */}
          <div className="bg-white p-4 sm:p-6 rounded-xl border border-[#09517d] flex flex-col items-center transition-shadow duration-300">
            <div className="flex items-center justify-center mb-3 sm:mb-4">
              <PayrollCardIcon className="w-6 h-6 sm:w-7 sm:h-7 text-[#09517d] mr-2" />
              <h3 className="text-lg sm:text-xl font-semibold text-[#09517d]">
                + Add Payroll Account
              </h3>
            </div>
            <p className="text-sm text-gray-600 text-center mb-4 sm:mb-6">
              Verify your income by signing in to your company's payroll
              account.
            </p>

            <button
              onClick={() => handleAddAccountClick("payroll")}
              disabled={isPlaidPayrollConnected}
              type="button"
              className={`
                  ${
                    isPlaidPayrollConnected
                      ? "bg-green-700 hover:bg-green-700 cursor-not-allowed"
                      : "bg-blue-500 hover:bg-blue-600"
                  }
                  text-white font-semibold py-2 sm:py-2.5 px-6 sm:px-8 rounded-lg
                  transition-all duration-200 ease-in-out
                  focus:outline-none focus:ring-2 focus:ring-opacity-50 mb-4
                  disabled:bg-green-600
                `}
              aria-label="Add Payroll Account"
            >
              {isPlaidPayrollConnected ? (
                <div className="flex items-center gap-1">
                  <CheckIcon className="w-5 h-5 text-white flex-shrink-0" />{" "}
                  Payroll Account Connected
                </div>
              ) : (
                "Add Account via Plaid"
              )}
            </button>

            {/* Upload Section for Payroll Documents */}
            <div className="mt-2 pt-4 border-t border-gray-200 w-full space-y-3">
              <h4 className="text-md font-semibold text-gray-700 text-center">
                Or, Upload Payroll Documents
              </h4>
              <FileDropzone isLoader={isLoader} docType="payroll" />
              <button
                type="button"
                className="w-full flex items-center justify-center p-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors md:hidden disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleTakePhotoClick("payroll-camera-input")}
                disabled={isLoader}
                aria-label="Take photo to upload payroll documents"
              >
                <CameraIcon className="w-5 h-5 text-gray-500 mr-2" />
                <span className="hidden md:block text-sm font-medium text-gray-700">
                  Take Photo
                </span>
                <span className="block md:hidden text-sm font-medium text-gray-700">
                  Tap to take photo
                </span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  id="payroll-camera-input"
                  className="sr-only"
                  onChange={(e) => handleFileChange(e, "payroll")}
                  disabled={isLoader}
                />
              </button>
            </div>
          </div>
        </div>
        {showSubmitButton && (
          <div className="mt-8 sm:mt-10 text-right hidden md:block">
            <button
              onClick={onNextStep}
              type="button"
              disabled={isLoader}
                          className="bg-[#09517d] hover:bg-[#084a6f] text-white font-bold py-2.5 px-6 rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#09517d] focus:ring-opacity-50 text-base lg:text-[1.2rem] lg:leading-[2rem] disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Proceed to next step"
            >
              {isLastStep ? "Submit" : "Next"}
            </button>
          </div>
        )}
      </div>

      {linkToken && (
        <PlaidLinkWrapper
          key={linkToken} // <-- forces Plaid to reinitialize
          token={linkToken}
          onSuccess={onPlaidSuccess}
          onExit={onPlaidExit}
          onEvent={onPlaidEvent}
        />
      )}
    </main>
  );
};

export default UpdateIncomeContent;
